import {
  CheckCircleOutlineRounded as LoadedIcon,
  ErrorOutlineRounded as ErrorIcon,
  HourglassEmptyRounded as PendingIcon,
  OpenInNewRounded as OpenInNewIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as strings from "common/strings";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import BigLoader from "components/common/BigLoader";
import InlineSearch from "components/common/InlineSearch";
import DashboardContent from "components/layout/DashboardContent";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useListCompaniesQuery } from "services/adminService";
import { useLazyListUsersQuery } from "services/adminService";

const COMPANY_STATE = {
  PENDING: "pending",
  LOADING: "loading",
  LOADED: "loaded",
  ERROR: "error",
};

const avatarSx = {
  width: "2rem",
  height: "2rem",
  fontSize: "0.875rem",
  fontWeight: 600,
};

const emailForColorLookup = (email) =>
  (email ?? "").replace(/\+[^@]*/, "") || (email ?? "");

const readablePermissionName = (permission) =>
  strings.toSentenceCase(
    permission.replace("web:", "").replace("forms:", "Forms_")
  );

const sortUsersByEmailThenCompany = (a, b) => {
  const emailA = (a.email ?? "").toLowerCase();
  const emailB = (b.email ?? "").toLowerCase();
  if (emailA !== emailB) return emailA.localeCompare(emailB);
  const companyA = (a.companyId ?? "").toLowerCase();
  const companyB = (b.companyId ?? "").toLowerCase();
  return companyA.localeCompare(companyB);
};

const UserCell = ({ email, userId }) => {
  const displayEmail = email ?? "";
  const emailMeta = strings.extractNameFromEmail(displayEmail);
  const color = strings.stringToColor(emailForColorLookup(displayEmail));

  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      gap={1}
      sx={{ width: "100%", minWidth: 0 }}
    >
      <Avatar sx={{ ...avatarSx, bgcolor: color, flexShrink: 0 }}>
        {emailMeta.initials}
      </Avatar>
      <Stack minWidth={0} sx={{ flex: 1 }}>
        <Typography variant={"body2"} noWrap>
          {emailMeta.name}
        </Typography>
        <Typography
          variant={"caption"}
          sx={{ marginTop: "-3px", color }}
          noWrap
        >
          {displayEmail || "—"}
        </Typography>
        {userId && (
          <Typography variant={"caption"} color={"text.secondary"} noWrap>
            {userId}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const AllUsersPage = () => {
  const {
    data: companiesData,
    isLoading: companiesLoading,
    isError: companiesError,
  } = useListCompaniesQuery();
  const [triggerListUsers] = useLazyListUsersQuery();

  const companyIds = useMemo(
    () => (companiesData?.map((c) => c.id) ?? []).filter(Boolean).sort(),
    [companiesData]
  );

  const [allUsers, setAllUsers] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [companyStates, setCompanyStates] = useState({});

  const setCompanyState = useCallback((companyId, state) => {
    setCompanyStates((prev) => ({ ...prev, [companyId]: state }));
  }, []);

  useEffect(() => {
    if (companyIds.length === 0) return;

    let cancelled = false;
    const fetchAll = async () => {
      setCompanyStates(
        Object.fromEntries(companyIds.map((id) => [id, COMPANY_STATE.PENDING]))
      );

      const queue = [...companyIds];
      const combined = [];

      const worker = async () => {
        while (queue.length && !cancelled) {
          const companyId = queue.shift();
          setCompanyState(companyId, COMPANY_STATE.LOADING);
          try {
            const result = await triggerListUsers({ companyId });
            if (cancelled) return;
            if (result.data && Array.isArray(result.data)) {
              combined.push(
                ...result.data.map((u) => ({
                  ...u,
                  companyId: u.companyId ?? companyId,
                }))
              );
              setCompanyState(companyId, COMPANY_STATE.LOADED);
            } else {
              setCompanyState(companyId, COMPANY_STATE.ERROR);
            }
          } catch {
            if (!cancelled) setCompanyState(companyId, COMPANY_STATE.ERROR);
          }
        }
      };

      const concurrency = Math.min(5, companyIds.length);
      await Promise.all(Array.from({ length: concurrency }, worker));
      if (!cancelled) setAllUsers(combined);
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [companyIds, triggerListUsers, setCompanyState]);

  const filteredUsers = useMemo(() => {
    const query = (searchFilter ?? "").trim().toLowerCase();
    if (!query) {
      return [...allUsers].sort(sortUsersByEmailThenCompany);
    }
    const filtered = allUsers.filter((u) => {
      const email = (u.email ?? "").toLowerCase();
      const company = (u.companyId ?? "").toLowerCase();
      const perms = u.perms ?? [];

      const roleText = [...perms.map(readablePermissionName), ...perms]
        .join(" ")
        .toLowerCase();
      const status = u.enabled === true ? "active" : "inactive";
      return (
        email.includes(query) ||
        company.includes(query) ||
        roleText.includes(query) ||
        status.includes(query)
      );
    });
    return [...filtered].sort(sortUsersByEmailThenCompany);
  }, [allUsers, searchFilter]);

  const loadingCompanies = companiesLoading;
  const loadingUsers = companyIds.length > 0 && allUsers.length === 0;
  const showCompanyProgress =
    !loadingCompanies && companyIds.length > 0 && loadingUsers;

  const getCompanyStateIcon = (state) => {
    switch (state) {
      case COMPANY_STATE.LOADING:
        return <CircularProgress size={20} />;
      case COMPANY_STATE.LOADED:
        return <LoadedIcon color={"success"} fontSize={"small"} />;
      case COMPANY_STATE.ERROR:
        return <ErrorIcon color={"error"} fontSize={"small"} />;
      default:
        return <PendingIcon color={"action"} fontSize={"small"} />;
    }
  };

  if (companiesError) {
    return (
      <AdminDashboard>
        <DashboardContent isFullWidth={false}>
          <Box sx={{ margin: 2, textAlign: "center" }}>
            <Typography variant={"subtitle1"}>
              {"Failed to load companies list."}
            </Typography>
          </Box>
        </DashboardContent>
      </AdminDashboard>
    );
  }

  if (loadingCompanies) {
    return (
      <AdminDashboard>
        <DashboardContent isFullWidth={false}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40vh",
              width: "100%",
            }}
          >
            <BigLoader />
          </Box>
        </DashboardContent>
      </AdminDashboard>
    );
  }

  if (showCompanyProgress) {
    return (
      <AdminDashboard>
        <DashboardContent isFullWidth={false}>
          <Component title={"All Users"} subtitle={"Loading users per company"}>
            <Paper variant={"outlined"} sx={{ padding: 1 }}>
              <List dense>
                {companyIds.map((companyId) => (
                  <ListItem key={companyId}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getCompanyStateIcon(
                        companyStates[companyId] ?? COMPANY_STATE.PENDING
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={companyId}
                      secondary={
                        companyStates[companyId] === COMPANY_STATE.LOADED
                          ? "Loaded"
                          : companyStates[companyId] === COMPANY_STATE.ERROR
                          ? "Error"
                          : companyStates[companyId] === COMPANY_STATE.LOADING
                          ? "Loading…"
                          : "Pending"
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Component>
        </DashboardContent>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={false}>
        <Component
          title={"All Users"}
          options={
            <InlineSearch
              placeholder={"Quick filter"}
              onChange={(value) => setSearchFilter(value ?? "")}
            />
          }
        >
          <TableContainer
            component={Paper}
            variant={"outlined"}
            sx={{ maxHeight: "calc(100vh - 12rem)" }}
          >
            <Table size={"small"} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {"User"}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {"Company"}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {"Roles"}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "background.paper",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {"Status"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  const companyId = user.companyId ?? "";
                  return (
                    <TableRow key={`${companyId}-${user.username}`}>
                      <TableCell>
                        <UserCell email={user.email} userId={user.username} />
                      </TableCell>
                      <TableCell>
                        {companyId ? (
                          <MuiLink
                            href={`/admin/${companyId}/users`}
                            target={"_blank"}
                            rel={"noopener noreferrer"}
                            underline={"hover"}
                            color={"inherit"}
                            sx={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              "&:hover": {
                                color: "primary.main",
                              },
                              "& .open-in-new-icon": {
                                opacity: 0,
                              },
                              "&:hover .open-in-new-icon": {
                                opacity: 1,
                              },
                            }}
                          >
                            {companyId}
                            <OpenInNewIcon
                              className={"open-in-new-icon"}
                              sx={{ fontSize: "1rem" }}
                            />
                          </MuiLink>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        {(user.perms ?? [])
                          .map(readablePermissionName)
                          .join(", ") || "—"}
                      </TableCell>
                      <TableCell>
                        {user.enabled ? "Active" : "Inactive"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredUsers.length === 0 && (
            <Box sx={{ padding: 4, textAlign: "center" }}>
              <Typography color={"text.secondary"}>
                {searchFilter.trim()
                  ? `No users match "${searchFilter}".`
                  : "No users found."}
              </Typography>
            </Box>
          )}
        </Component>
      </DashboardContent>
    </AdminDashboard>
  );
};

export default AllUsersPage;
